import * as $ from "jquery";
import { chain } from "lodash";
import { commonClientResx } from "~/resxDist/commonClientResx";
import { showFeedback } from "~/sharedComponents/feedback";
import * as styles from "./toastr.less";

export enum notificationStates {
    saving = "saving",
    saved = "saved",
    failed = "failed",
}

const globalStyles = {
    saving: "toast-info",
    saved: "toast-success",
    failed: "toast-error",
};

interface ToastCssClasses {
    saved: boolean;
    saving: boolean;
    failed: boolean;

    [k: string]: boolean;
}

interface ToastrState {
    message: string;
    toastCssClasses: ToastCssClasses;
}

const hideTimeout = 2500;
let instance: SavingNotifier = null;

abstract class BaseToastNotifier {
    currentToastElem: JQuery<HTMLElement>;
    hideTimer: number;

    constructor() {
        this.currentToastElem = null;
        this.hideTimer = null;
    }

    hideToast() {
        showFeedback.clear(this.currentToastElem);
        ep.timer.clearTimeout(this.hideTimer);
        this.hideTimer = null;
        this.currentToastElem = null;
    }

    showToast(state: notificationStates) {
        const stateObject = this.getStateObject(state);

        if (this.hideTimer) {
            ep.timer.clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }

        if (state !== notificationStates.saving) {
            this.hideTimer = ep.timer.setTimeout(this.hideToast.bind(this), hideTimeout);
        }

        if (this.currentToastElem) {
            this.currentToastElem.find(`.${styles.text}`).text(stateObject.message);
            this._setToastStyles(stateObject.toastCssClasses);
            return;
        }

        const messageElem = $("<span></span>").addClass(styles.text).text(stateObject.message);

        const iconElem = $("<div></div>").addClass(styles.icon);
        const toastContent = messageElem[0].outerHTML + iconElem[0].outerHTML;
        const options = this.initToastOptions();

        this.currentToastElem = showFeedback.action(toastContent, null, options);

        this._setToastStyles(stateObject.toastCssClasses);
    }

    getStateObject(state: notificationStates): ToastrState {
        const stateObject: ToastrState = {
            message: commonClientResx[state],
            toastCssClasses: {
                saved: false,
                saving: false,
                failed: false,
            },
        };

        stateObject.toastCssClasses[state] = true;
        return stateObject;
    }

    _setToastStyles(cssClassNames: ToastCssClasses) {
        if (!this.currentToastElem) {
            return;
        }

        chain(cssClassNames)
            .keys()
            .each(className =>
                this.currentToastElem.toggleClass((globalStyles as any)[className], cssClassNames[className])
            )
            .value();
    }

    initToastOptions() {
        const hideCallback = () => this.hideToast();

        const toastOptions = {
            showDuration: 0,
            hideDuration: 500,
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: false,
            closeOnHover: false,
            onHidden: hideCallback,
            escapeHtml: false,
        };

        return toastOptions;
    }
}

export class SavingNotifier extends BaseToastNotifier {
    allCompleted: JQuery.Deferred<any>;
    promisesArray: any[];

    constructor() {
        super();
        this.allCompleted = $.Deferred();
        this.promisesArray = [];
    }

    static get instance(): SavingNotifier {
        return instance || (instance = new SavingNotifier());
    }

    static destroy() {
        if (!instance) {
            return;
        }
        instance.promisesArray = [];
        instance.hideToast();
        instance = null;
    }

    static registerPromise(promise: Promise<any>) {
        SavingNotifier.instance.addTrackingPromise(promise);
    }

    addTrackingPromise(promise: Promise<any>) {
        this.allCompleted.reject({ skipShowToast: true });

        this.promisesArray.push(promise);

        const defer = (this.allCompleted = $.Deferred());

        this.showToast(notificationStates.saving);

        $.when(...this.promisesArray)
            .done(() => {
                defer.resolve();
            })
            .fail(() => {
                defer.reject();
            });

        defer
            .done(() => {
                this.showToast(notificationStates.saved);
            })
            .fail((res: { skipShowToast: boolean }) => {
                if (res && res.skipShowToast) {
                    return;
                }
                this.showToast(notificationStates.failed);
            })
            .always(() => {
                this.promisesArray = [];
            });
    }
}

export const withSavingNotifier = (xhr: Promise<any>) => {
    SavingNotifier.registerPromise(xhr);
    return xhr;
};

class PromiseTracker extends BaseToastNotifier {
    runningText: string;
    finishedText: string;
    failedText: string;
    hideFinishedFeedback: boolean;

    constructor(
        promise: PromiseLike<any>,
        runningText: string,
        finishedText: string,
        failedText: string,
        hideFinishedFeedback = false
    ) {
        super();

        this.runningText = runningText;
        this.finishedText = finishedText;
        this.failedText = failedText;
        this.hideFinishedFeedback = hideFinishedFeedback;

        this.showToast(notificationStates.saving);

        promise.then(
            () => {
                if (!this.hideFinishedFeedback) {
                    this.showToast(notificationStates.saved);
                } else {
                    this.hideToast();
                }
            },
            () => {
                this.showToast(notificationStates.failed);
            }
        );
    }

    getStateObject(state: notificationStates): ToastrState {
        const stateObject = super.getStateObject(state);

        switch (state) {
            case notificationStates.saving:
                stateObject.message = this.runningText;
                break;
            case notificationStates.saved:
                stateObject.message = this.finishedText;
                break;
            case notificationStates.failed:
                stateObject.message = this.failedText;
                break;
        }
        return stateObject;
    }
}

export type PromiseNotifierArgs = {
    runningText?: string;
    finishedText?: string;
    failedText?: string;
    hideFinishedFeedback?: boolean;
};
export const withPromiseNotifier = (
    promise: PromiseLike<any>,
    {
        runningText = commonClientResx.saving,
        finishedText = commonClientResx.saved,
        failedText = commonClientResx.failed,
        hideFinishedFeedback,
    }: PromiseNotifierArgs = {}
) => {
    return new PromiseTracker(promise, runningText, finishedText, failedText, hideFinishedFeedback);
};
